/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useHeaderData } from '@/features/header/api/getHeader';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { useAppState } from '@/stores/AppState';
import { type GetRecommendationsParamsModel } from '@/api/types/product-recommendations-types';

type UseRecommendationParamsOptions = {
  servingConfig: string;
  productId: string;
  recordsPerPage?: number;
  validateOnly?: boolean;
};

/**
 * Gathers all required parameters for recommendations API call
 * Sources data from multiple hooks and validates vehicle parameters
 */
export const useRecommendationParams = ({
  servingConfig,
  productId,
  recordsPerPage = 24,
  validateOnly,
}: UseRecommendationParamsOptions): GetRecommendationsParamsModel => {
  const { state } = useAppState();

  // Get header and store data
  const { data: headerData } = useHeaderData();
  const { data: storeDetailsData } = useStoreDetailsData();

  // Get vehicle data
  const preferredVehicle = usePreferredVehicle();

  // Extract vehicle questions from ACES attributes
  const vehicleQuestions = extractVehicleQuestions(
    headerData?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds,
    true, // asPipeSeparatedString
  ) as string;

  return {
    visitorId: state.visitorId ?? '',
    userId: headerData?.myAccountMap?.customerID,
    productId,
    servingConfig,
    country: 'USA',
    recordsPerPage,
    storeId: storeDetailsData?.storeNumber,
    validateOnly,
    ...(preferredVehicle && {
      year: String(preferredVehicle.year),
      makeId: String(preferredVehicle.makeId),
      modelId: String(preferredVehicle.modelId),
      vehicleTypeId: String(preferredVehicle.vehicleTypeId),
      vehicleQuestions: vehicleQuestions || undefined,
    }),
  };
};
